import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CognitoService, AuthUser } from '../services/cognitoService';

interface AuthContextType {
    user: AuthUser | null;
    loading: boolean;
    login: (user: AuthUser) => void;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuthState();

        // Handle auth callback from Cognito Hosted UI
        const handleAuthCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('code')) {
                try {
                    const user = await CognitoService.handleAuthCallback();
                    if (user) {
                        setUser(user);
                        localStorage.setItem('isAuthenticated', 'true');
                        // Clean up URL
                        window.history.replaceState({}, document.title, window.location.pathname);
                    }
                } catch (error) {
                    console.error('Error handling auth callback:', error);
                }
            }
        };

        handleAuthCallback();
    }, []);

    const checkAuthState = async () => {
        try {
            const session = await CognitoService.getCurrentSession();
            if (session && session.isValid()) {
                const cognitoUser = CognitoService.getCurrentUser();
                if (cognitoUser) {
                    setUser({
                        username: cognitoUser.getUsername(),
                        accessToken: session.getAccessToken().getJwtToken(),
                        idToken: session.getIdToken().getJwtToken(),
                        refreshToken: session.getRefreshToken().getToken(),
                    });
                }
            }
        } catch (error) {
            console.error('Error checking auth state:', error);
        } finally {
            setLoading(false);
        }
    };

    const login = (authUser: AuthUser) => {
        setUser(authUser);
        localStorage.setItem('isAuthenticated', 'true');
    };

    const logout = async () => {
        try {
            await CognitoService.signOut();
            setUser(null);
            localStorage.removeItem('isAuthenticated');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const value: AuthContextType = {
        user,
        loading,
        login,
        logout,
        isAuthenticated: user !== null,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};